import React, {useEffect, useMemo, useState} from 'react';
import {Box, Grid} from '@material-ui/core';
import DialogContainer, {DialogButton,} from '../../components/AlertDialog/DialogContainer';
import {useFormik} from 'formik';
import IconWithTooltip from '../../components/Tooltip/IconWithTooltip';
import TextField from '../../components/TextField/TextField';
import {useTranslation} from 'react-i18next';
import SelectField from '../../components/SelectField/SelectField';
import CustomKeyboardDateTimePicker from '../../components/KeyboardDateTimePicker/KeyboardDateTimePicker';
import {ReduxState} from '../../store/types';
import {ConfigData} from '../../store/types/ConfigData';
import {
    CreateNewPortalUserFormData,
    CreateProfileSupervisorValidationSchema,
    useStyles,
} from '../../components/PortalUsers/utils';
import {useDispatch, useSelector} from 'react-redux';
import classNames from 'classnames';
import {actions} from '../../store';
import Loader from '../../components/Loader/Loader';
import PasswordTextField from '../../components/PasswordTextField/PasswordTextField';
import {getConfigFromPasswordRulesOrDefault} from '../../utils/passwordGenerator';
import {PortalUsersErrors} from '../../services/apiErrors';
import {convertCurrentDateToUserTimeZone} from './utils';
import ExtensionAsyncSelectFiled from "../../components/CallControls/ExtensionAsyncSelectFiled";
import i18n from '../../services/i18n';
import {CreateNewPortalUserValidationSchema} from "./CreateNewPortalUserDialog.utils";
import {CustomerOfficeType} from "../../store/types/CustomerInfo";

interface CreateNewPortalUserDialogProps {
    isOpen: boolean;
    toggleVisibility?: () => void;
}

const CreateNewPortalUserDialog: React.VFC<CreateNewPortalUserDialogProps> = (
    {
        isOpen,
        toggleVisibility,
    }) => {

    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {
        roleList,
        isFormSaving,
        isAddPortalUserDataLoading,
        apiErrors,
    } = useSelector((state: ReduxState) => state.company);

    const {timeZonesList, languagesList} = useSelector(
        (state: ReduxState) => state.generic,
    );

    const [isSvTypeSelected, setIsSvTypeSelected] = useState(false)


    const config = useSelector<ReduxState, ConfigData | undefined>(
        (state) => state.generic?.configData,
    );

    const i_lang = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.i_lang || '',
    );
    const i_time_zone = useSelector<ReduxState, number>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.i_time_zone || 0,
    );
    const userDateFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.out_date_format ||
            '',
    );

    const i_office_type = useSelector<ReduxState, CustomerOfficeType | undefined>(
        (state) => state.generic?.customerOfficeType
    );


    const today = new Date();
    today.setDate(today.getDate());

    const initialValues = useMemo(
        () => ({
            login: '',
            password: '',
            role: '',
            email: '',
            activationDate: null,
            expirationDate: null,
            timezone: timeZonesList.find((e) => e.i_time_zone === i_time_zone)
                ?.time_zone_name,
            language: languagesList?.find((e) => e.iso_639_1 === i_lang)?.name,
            extension: undefined
        }),
        [i_lang, i_time_zone, timeZonesList, languagesList],
    );


    const validationSchema = useMemo(() => {

        if (isSvTypeSelected) {
            return CreateNewPortalUserValidationSchema(config).concat(CreateProfileSupervisorValidationSchema)
        }

        return CreateNewPortalUserValidationSchema

    }, [isSvTypeSelected])

    const {
        values,
        handleSubmit,
        handleChange,
        resetForm,
        setFieldValue,
        setFieldError,
        errors,
        dirty,
    } = useFormik<CreateNewPortalUserFormData>({
        initialValues,
        onSubmit: (form) => {
            dispatch(
                actions.saveNewPortalUser.request({
                    login: form.login,
                    password: form.password,
                    email: form.email,
                    i_role:
                        roleList?.find((e) => e.name === form.role)?.i_role ||
                        0,
                    activation_date: form.activationDate,
                    expiration_date: form.expirationDate,
                    i_time_zone: timeZonesList.find(
                        (e) => e.time_zone_name === form.timezone,
                    )?.i_time_zone,
                    lang: languagesList?.find((e) => e.name === form.language)
                        ?.iso_639_1,
                    i_account: isSvTypeSelected ? form.extension?.i_account : undefined,
                    callback: toggleVisibility,
                }),
            );
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

    });
    
    const filteredLanguages = useMemo(() => {
        const langCodesAllowed = i18n?.options?.resources ? Object.keys(i18n?.options?.resources) : ['en'];
        const langs = languagesList
            ?.filter(e => langCodesAllowed?.findIndex(c => c.toLocaleLowerCase()
                .replace(/-_\s /gi, '') === e.iso_639_1.toLocaleLowerCase().replace(/-_\s /gi, '')) !== -1) 
            ?? [];
        return langs;
    }, [languagesList, i18n, t]);
    
    useEffect(() => {
        if (isOpen) {
            resetForm();
            dispatch(actions.getAddPortalUserData.request());
        }
    }, [isOpen]);

    useEffect(() => {
        if (
            apiErrors?.faultstring.includes(PortalUsersErrors.PortalUserExist)
        ) {
            setFieldError(
                'login',
                t('errors:portalUsers.loginExists', {value: values.login}),
            );
        }


        if (
            apiErrors?.faultstring.includes(PortalUsersErrors.SelectedAccountExist)
        ) {

            setFieldError(
                'extension',
                t('errors:portalUsers.accountIsUsed'));
        }

    }, [apiErrors]);

    const handleCancel = () => {
        toggleVisibility?.();
    };
    const isSaveDisabled = useMemo(
        () => !values.login || !values.password || !values.role,
        [values],
    );

    const isSaveDisabledForSv = useMemo(
        () => !values.login || !values.password || !values.role || !values.extension,
        [values],
    );

    const minForExpiration = useMemo(() => {
        const maxDate = convertCurrentDateToUserTimeZone(values.timezone);
        if (!values.activationDate) {
            return maxDate;
        }
        const minday = new Date(values.activationDate.toString());
        minday.setDate(minday.getDate() + 1);
        return minday;
    }, [values.activationDate, values.timezone]);

    const minDateForActivation = useMemo(() => {
        const ret = convertCurrentDateToUserTimeZone(values.timezone);
        return ret;
    }, [timeZonesList, values.timezone]);


    const formatedRoleList = useMemo(() => {

        return roleList
            ?.filter((e) => e.name !== t('common:any', 'Any'))
            ?.filter(e => i_office_type === CustomerOfficeType.Branch ? e.i_role_type !== 7 : true)
            ?.map((v) => v.name) || []
    }, [roleList])


    return (
        <DialogContainer
            isOpen={isOpen}
            dataQa="sip-dialog"
            header={t('screens:portalUsers.addNewPortalUser')}
            headerClass={classes.header}
            className={classes.modalContainer}
            dialogActionsButtons={[
                <DialogButton
                    key="cancel"
                    label={t('common:cancel')}
                    onClick={handleCancel}
                />,
                <DialogButton
                    className={classNames(
                        !dirty || isSaveDisabled
                            ? classes.primaryActionButton
                            : classes.saveButton,
                    )}
                    disabled={!dirty || (isSvTypeSelected ? isSaveDisabledForSv : isSaveDisabled)}
                    key="save"
                    label={t('common:save')}
                    onClick={handleSubmit}
                />,
            ]}
        >
            <form
                onSubmit={handleSubmit}
                autoComplete="off"
                data-testid="create-extension-form"
            >
                <Grid
                    item
                    className={classNames(
                        classes.itemsContainer,
                        classes.marginNone,
                    )}
                >
                    <Box className={classes.headerBox} width={350}>
                        <span className={classes.rowBoxHeader}>
                            {t('screens:myCompany.general')}
                        </span>

                        <IconWithTooltip
                            dataQa="my-company-address"
                            tooltipText={t('tooltips:myCompany.general')}
                        />
                    </Box>
                    <Box className={classes.rowBox}>
                        <TextField
                            id="login"
                            label={t('common:login')}
                            onChange={handleChange}
                            value={values.login}
                            setFieldError={setFieldError}
                            dataQa="create-portal-user-login"
                            helperText={errors?.login}
                            required
                            maxLength={64}
                        />
                        <PasswordTextField
                            id="password"
                            label={t('common:password')}
                            value={values.password}
                            onChange={handleChange}
                            dataQa="create-extension-name-input"
                            setFieldError={setFieldError}
                            error={errors.password}
                            isRequired={true}
                            passwordRulesConfig={getConfigFromPasswordRulesOrDefault(
                                config,
                                true
                            )}
                            setFieldValue={setFieldValue}
                            maxLength={32}
                            inputProps={{
                                inputProps: {
                                    min:
                                        config?.Accounts
                                            .CreditAccMinServicePasswdLength ||
                                        0,
                                    maxLength: 32,
                                },
                            }}
                        />
                    </Box>
                    <Box className={classes.rowBox}>
                        <TextField
                            id="email"
                            label={t('common:emailAddress')}
                            onChange={handleChange}
                            value={values.email}
                            setFieldError={setFieldError}
                            dataQa="create-portal-user-email"
                            helperText={errors?.email}
                            maxLength={128}
                        />

                        <SelectField
                            id="role"
                            onChange={(e, v) => {
                                setFieldValue('role', v)
                                const role = roleList?.find(r => r.name == v)
                                setFieldError('extension', undefined)
                                if (role?.i_role_type == 7) {
                                    setIsSvTypeSelected(true)
                                } else {
                                    setIsSvTypeSelected(false)
                                }
                            }}
                            label={t('screens:portalUsers.role')}
                            items={
                                formatedRoleList
                            }
                            value={values.role}
                            dataQa="create-portal-user-role"
                            icon={
                                roleList?.find((e) => e.name === values.role)
                                    ?.description ? (
                                    <IconWithTooltip
                                        dataQa="my-company-address"
                                        tooltipText={
                                            roleList?.find(
                                                (e) => e.name === values.role,
                                            )?.description || ''
                                        }
                                    />
                                ) : undefined
                            }
                            helperText={errors?.role}
                            required
                            setFieldError={setFieldError}
                        />
                    </Box>

                    {roleList?.find(role => role.name == values.role)?.i_role_type == 7 && (
                        <Box className={classes.rowBox}>
                            <ExtensionAsyncSelectFiled
                                id={'extension'}
                                // @ts-ignore
                                value={values.extension}
                                setValue={(value) => {
                                    setFieldValue('extension', value)
                                }}
                                customClass={classes.extensionField}
                                required={true}
                                useAny={false}
                                showRegisterStatus={false}
                                disableClearable={true}
                                forceEnable={true}
                                useExtensionFilter={true}
                                setFieldError={setFieldError}
                                helperText={errors.extension}
                            />
                        </Box>
                    )}

                </Grid>


                <Grid
                    item
                    className={classNames(
                        classes.itemsContainer,
                        classes.marginBottom,
                    )}
                >
                    <Box className={classes.headerBox} width={350}>
                        <span className={classes.rowBoxHeader}>
                            {t('screens:portalUsers.changeStatus')}
                        </span>

                        <IconWithTooltip
                            dataQa="my-company-address"
                            tooltipText={t('tooltips:myCompany.changeStatus')}
                        />
                    </Box>
                    <Box
                        className={classNames(
                            classes.rowBox,
                            classes.smallerMargin,
                        )}
                    >
                        <CustomKeyboardDateTimePicker
                            id="activationDate"
                            label={t('screens:portalUsers.activationDate')}
                            value={values.activationDate}
                            onChange={(v) => setFieldValue('activationDate', v)}
                            dataQa={'portal-user-activation-date'}
                            dataTestId={'portal-user-activation-date'}
                            minDate={minDateForActivation}
                            withHours={false}
                            allowEmptyDate={true}
                            userDateTimeFormat={userDateFormat}
                            setFieldError={setFieldError}
                            helperText={errors?.activationDate}
                        />
                        <CustomKeyboardDateTimePicker
                            id="expirationDate"
                            label={t('screens:portalUsers.expirationDate')}
                            value={values.expirationDate}
                            onChange={(v) => setFieldValue('expirationDate', v)}
                            dataQa={'portal-user-expieration-date'}
                            dataTestId={
                                'portal-user-activexpierationation-date'
                            }
                            withHours={false}
                            allowEmptyDate={true}
                            minDate={minForExpiration}
                            userDateTimeFormat={userDateFormat}
                            setFieldError={setFieldError}
                            helperText={errors?.expirationDate}
                        />
                    </Box>
                    <Box className={classes.rowBox}>
                        <SelectField
                            id="timezone"
                            onChange={(e, v) => setFieldValue('timezone', v)}
                            label={t('common:timezone')}
                            items={
                                timeZonesList?.map((v) => v.time_zone_name) ||
                                []
                            }
                            value={values.timezone || null}
                            dataQa="create-portal-user-timezone"
                        />

                        <SelectField
                            id="language"
                            onChange={(e, v) => setFieldValue('language', v)}
                            label={t('common:language')}
                            items={filteredLanguages?.map((v) => v.name) || []}
                            value={values.language || null}
                            dataQa="create-portal-user-language"
                        />
                    </Box>
                </Grid>
            </form>
            {(isFormSaving || isAddPortalUserDataLoading) && (
                <Loader
                    dataQa="create-auto-attendant-data-fetching-loader"
                    absolutePosition
                />
            )}
        </DialogContainer>
    );
};

export default CreateNewPortalUserDialog;
